// Same rules as the API's src/jobCards/data/jobCardTotals.js: keep the two in step
const priceBeforeLimits = (item, fallbackDieselPrice) => {
    const discount = 1 - ((item.discount ?? 0) / 100);
    const price = item.vehicleItem
        ? (item.dieselUsed ?? 0) * (item.roadConditionRate?.rate ?? 0) * (item.dieselPrice ?? fallbackDieselPrice ?? 0)
        : (item.quantity ?? 0) * (item.currentUnitPrice ?? 0);
    return discount * price;
};

// CCIP lines keep the minimum and maximum fee that applied when they were priced
export const lineTotal = (item, fallbackDieselPrice) =>
    Math.min(Math.max(priceBeforeLimits(item, fallbackDieselPrice), item.minimumFee ?? -Infinity), item.maximumFee ?? Infinity);

export const jobCardTotal = (items, fallbackDieselPrice) => (items ?? []).reduce((sum, item) => sum + lineTotal(item, fallbackDieselPrice), 0);

// 'min' or 'max' when a CCIP fee limit set the line's price, otherwise null
export const feeLimit = (item, fallbackDieselPrice) => {
    const price = priceBeforeLimits(item, fallbackDieselPrice);
    if (item.minimumFee != null && price < item.minimumFee) return 'min';
    if (item.maximumFee != null && price > item.maximumFee) return 'max';
    return null;
};

// How a line price shows a fee limit: orange for the minimum, green for the maximum, with a hover note
const FEE_LIMIT_DISPLAY = {
    min: { sx: { color: 'warning.main', fontWeight: 600 }, title: 'Minimum CCIP fee applied' },
    max: { sx: { color: 'success.main', fontWeight: 600 }, title: 'Maximum CCIP fee applied' }
};
export const feeLimitDisplay = (item, fallbackDieselPrice) => FEE_LIMIT_DISPLAY[feeLimit(item, fallbackDieselPrice)] ?? { sx: {}, title: undefined };

// The fee limits from job card settings, stored on a CCIP line when it is priced
export const ccipFees = (settings) => ({
    minimumFee: settings?.ccipPricing?.ccipMinimumFee ?? null,
    maximumFee: settings?.ccipPricing?.ccipMaximumFee ?? null
});

export const isCcip = (itemName) => (itemName ?? '').trim().toUpperCase() === 'CCIP';

// Today's CCIP price per ton: minimum grams per ton x gold price per gram, from job card settings
export const ccipUnitPrice = (settings) => {
    const grams = parseFloat(settings?.ccipPricing?.ccipMinGramsPerTon ?? 0);
    const gold = parseFloat(settings?.ccipPricing?.ccipGoldPricePerGram ?? 0);
    return (Number.isFinite(grams) ? grams : 0) * (Number.isFinite(gold) ? gold : 0);
};

// A line's price fields for a newly picked item: CCIP from today's settings with its fee limits,
// anything else at the item's list price
export const priceFromItem = (option, settings) => (isCcip(option.itemName)
    ? { currentUnitPrice: ccipUnitPrice(settings), ...ccipFees(settings) }
    : { currentUnitPrice: option.currentUnitPrice, minimumFee: null, maximumFee: null });

// A line with the quantity that was typed; CCIP tonnage keeps 4 decimals and is repriced from today's settings
export const withQuantity = (line, typed, settings, itemName = line.itemName) => {
    const value = parseFloat(typed);
    const quantity = Number.isFinite(value) ? value.toFixed(isCcip(itemName) ? 4 : 2) : null;
    return { ...line, quantity, ...(isCcip(itemName) ? { currentUnitPrice: ccipUnitPrice(settings), ...ccipFees(settings) } : {}) };
};
